import {AttrsSchema, Component, ConfigOptions, jsx} from 'panel';
import type {VNode} from 'snabbdom';

import {baseComparator} from 'common/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {fuzzyMatch} from 'common/util/fuzzy-match';
import {InputEl} from 'common/components/input';
import {ItemsMenuEl} from 'common/components/items-menu';
import {SectionItem} from 'common/components/items-menu/types';
import {SegmentLiftBaselineOption} from 'common/report/util/lift/types';
import {OrderBy} from 'common/util/array';

import css from './index.inline.styl';

interface LiftBaselineMenuAttrs {
  'baseline-options': SegmentLiftBaselineOption[];
  segment: string;
}

interface LiftBaselineMenuState {
  keydownProxyEl: Node | HTMLElement;
  value: string;
}

interface LiftBaselineMenuJsxProps {
  attrs: LiftBaselineMenuAttrs;
  on?: {
    select?: (ev: CustomEvent<{value: SegmentLiftBaselineOption}>) => void;
  };
}

export class LiftBaselineMenu extends Component<LiftBaselineMenuState, LiftBaselineMenuAttrs> {
  static get attrsSchema(): AttrsSchema<LiftBaselineMenuAttrs> {
    return {
      ...super.attrsSchema,
      'baseline-options': `json`,
      segment: `string`,
    };
  }

  get config(): ConfigOptions<LiftBaselineMenuState> {
    return {
      defaultState: {
        value: ``,
        keydownProxyEl: null,
      },
      template: () => this.renderTemplate(),
      useShadowDom: true,
      css,
    };
  }

  renderTemplate(): VNode {
    return (
      <div sel='.lift-baseline-menu'>
        <InputEl
          attrs={{
            elref: `baseline-input`,
            'auto-focus': true,
            placeholder: `Search`,
            'show-clear': true,
            icon: `search`,
          }}
          props={{
            value: this.state.value,
          }}
          on={{
            input: (ev) => this.handleInputChange(ev),
            clear: () => this.update({value: ``}),
          }}
          hook={{
            insert: (ev) =>
              this.update({
                keydownProxyEl: ev.elm,
              }),
          }}
        />
        <ItemsMenuEl
          attrs={{
            elref: `baseline-items`,
            'search-filter': this.state.value,
            sections: [
              {
                label: this.attr(`segment`),
                items: this.getFilteredSections(),
              },
            ] as SectionItem[],
          }}
          props={{
            keydownProxyEl: this.state.keydownProxyEl as HTMLElement,
          }}
          on={{
            clickItem: (ev) =>
              this.dispatchEvent(
                new CustomEvent(`select`, {
                  detail: {
                    value: ev.detail.value,
                  },
                }),
              ),
          }}
        />
      </div>
    );
  }

  getFilteredSections() {
    return this.attr(`baseline-options`)
      .map((option) => ({
        option,
        score: fuzzyMatch(option.displayName, this.state.value),
      }))
      .filter((prop) => prop.score)
      .sort(
        baseComparator<{score: number}, number>({order: OrderBy.Descending, transform: (prop) => prop.score}),
      )
      .map((baselineV) => ({value: baselineV.option, label: baselineV.option.displayName}));
  }

  handleInputChange(ev): void {
    this.update({
      value: ev.target.value,
    });
  }
}

export const LiftBaselineMenuEl = defineAndMakeJsxFactory<LiftBaselineMenuJsxProps>(
  `mp-lift-baseline-menu`,
  LiftBaselineMenu,
);
export const LiftBaselineMenuReact = panel2react<LiftBaselineMenuJsxProps>(`mp-lift-baseline-menu`, LiftBaselineMenu);
